import { FC, useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import { Form, Textbox, useForm } from "capitalrx-components-2.0/lib/forms";
import { LoadingGroup, LoadingSkeleton, Message, Messages, useAlert } from "capitalrx-components-2.0/lib/general";
import { Stack } from "capitalrx-components-2.0/lib/layout";
import { Body, Bold, Subheading } from "capitalrx-components-2.0/lib/typography";

import { useCreatePriorAuthorizationCase, useGetPriorAuthorizationCaseQuery } from "@/App/api/priorAuthorization";
import { MemberEligibilityContext } from "@/App/context";
import { emailValidator, phoneNumberValidator } from "@/App/utils/validation";

interface RenewalRequestFormModal {
  readonly isVisible: boolean;
  readonly onClose: () => void;
  readonly caseId: number;
  readonly refresh?: () => void;
}

interface RenewalRequestForm {
  readonly email: string;
  readonly phoneNumber: string;
}

const TRANSLATION_PATH = "PRIOR_AUTHORIZATION_RENEWAL_REQUEST_MODAL";

export const RenewalRequestFormModal: FC<RenewalRequestFormModal> = ({ isVisible, onClose, caseId, refresh = () => {} }) => {
  const { t: translate } = useTranslation();
  const { memberEligibility } = useContext(MemberEligibilityContext);
  const form = useForm<RenewalRequestForm>();
  const alert = useAlert();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { mutateAsync: createPriorAuthorization } = useCreatePriorAuthorizationCase();

  const {
    data: priorAuthorizationCaseData,
    isLoading: paCaseIsLoading,
    isLoadingError: isLoadingPACaseError,
    isRefetching: paCaseIsRefetching,
    isError: isPAFetchingError,
  } = useGetPriorAuthorizationCaseQuery({ id: caseId });

  const onSubmitRenewalRequestForm = async (values: RenewalRequestForm) => {
    try {
      const createPriorAuthorizationResponse = await createPriorAuthorization({
        previous_case_id: caseId,
        contact_email: values.email,
        contact_phone: values.phoneNumber,
      });
      alert({
        message: createPriorAuthorizationResponse.message,
        type: createPriorAuthorizationResponse?.case_id ? "success" : "error",
        style: "toast",
      });
      refresh();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form.Modal
      canReset={false}
      title={translate(`${TRANSLATION_PATH}.submitRenewalRequest`)}
      visible={isVisible}
      onClose={onClose}
      form={form}
      onSubmit={onSubmitRenewalRequestForm}
      submitContent={translate(`${TRANSLATION_PATH}.submit`)}
      cancelContent={translate(`${TRANSLATION_PATH}.cancel`)}
      disabled={isSubmitting}
    >
      <div style={{ borderBottom: "1px solid #CFD9E1", paddingBottom: "16px" }}>
        <Stack>
          <Stack spacing="none">
            <Body>
              <Bold>{translate(`${TRANSLATION_PATH}.member`)}</Bold>
              {memberEligibility.member.first_name + " " + memberEligibility.member.last_name}
            </Body>
            <Body>
              <Bold>ID: </Bold>
              {memberEligibility.member.id.toString()}
            </Body>
          </Stack>
          <LoadingGroup loading={paCaseIsLoading || paCaseIsRefetching}>
            <LoadingGroup.Placeholder
              fallback={
                <>
                  <Stack spacing="none">
                    <Body>
                      <Bold>{translate(`${TRANSLATION_PATH}.drug`)}</Bold>
                      <LoadingSkeleton style={{ height: "20px", width: "150px" }} />
                    </Body>
                    <Body>
                      <Bold>{translate(`${TRANSLATION_PATH}.quantity`)}</Bold>
                      <LoadingSkeleton style={{ height: "20px", width: "150px" }} />
                    </Body>
                    <Body>
                      <Bold>{translate(`${TRANSLATION_PATH}.daySupply`)}</Bold>
                      <LoadingSkeleton style={{ height: "20px", width: "150px" }} />
                    </Body>
                  </Stack>
                  <Stack spacing="none">
                    <Body>
                      <Bold>{translate(`${TRANSLATION_PATH}.prescriber`)}</Bold>
                      <LoadingSkeleton style={{ height: "20px", width: "150px" }} />
                    </Body>
                    <Body>
                      <Bold>NPI: </Bold>
                      <LoadingSkeleton style={{ height: "20px", width: "150px" }} />
                    </Body>
                  </Stack>
                </>
              }
            />
          </LoadingGroup>
          {!(isPAFetchingError || isLoadingPACaseError || paCaseIsLoading || paCaseIsRefetching) && (
            <>
              <Stack spacing="none">
                <Body>
                  <Bold>{translate(`${TRANSLATION_PATH}.drug`)}</Bold>
                  {priorAuthorizationCaseData?.drug_name}
                </Body>
                <Body>
                  <Bold>{translate(`${TRANSLATION_PATH}.quantity`)}</Bold>
                  {priorAuthorizationCaseData?.drug_quantity}
                </Body>
                <Body>
                  <Bold>{translate(`${TRANSLATION_PATH}.daySupply`)}</Bold>
                  {priorAuthorizationCaseData?.drug_days_supply}
                </Body>
              </Stack>
              <Stack spacing="none">
                <Body>
                  <Bold>{translate(`${TRANSLATION_PATH}.prescriber`)}</Bold>
                  {priorAuthorizationCaseData?.prescriber_first_name + " " + priorAuthorizationCaseData?.prescriber_last_name}
                </Body>
                <Body>
                  <Bold>NPI: </Bold>
                  {priorAuthorizationCaseData?.prescriber_npi}
                </Body>
              </Stack>
            </>
          )}
        </Stack>
      </div>
      <Subheading>{translate(`${TRANSLATION_PATH}.contactInfo`)}</Subheading>
      <Stack>
        <Textbox.Field
          label={translate(`${TRANSLATION_PATH}.emailAddress`)}
          instructions="email@address.com"
          name="email"
          required
          validator={(value) => emailValidator(value, translate)}
        />
        <Textbox.Field
          label={translate(`${TRANSLATION_PATH}.phoneNumber`)}
          instructions="123-456-7890"
          name="phoneNumber"
          required
          validator={(value) => phoneNumberValidator(value, translate)}
        />
        <Message appearance={Messages.Informational} visible>
          <Body>
            {translate(`${TRANSLATION_PATH}.weMayContactYourPrescriber`)}
          </Body>
        </Message>
      </Stack>
    </Form.Modal>
  );
};
